import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../store/store';
import Header from '../../../../shared/components/layout/Header';
import ClassItem from '../../../../screens/ui/StudentClassItem';
import CustomLoader from '../../../../shared/components/ui/CustomLoader';
import { useStudentDashboard } from '../hooks/useStudentDashboard';
import { useAuthGuard } from '../../../../hooks/useTokenValidation';
import { useSearchCoursesMutation, SearchResult } from '../../../../store/api/searchApi';
import { useGetSubjectsQuery } from '../../../../store/api/masterApi';
import { useGetStudentAllClassesQuery, useGetStudentUpcomingClassesQuery, StudentClass } from '../../../../store/api/studentApi';
import { 
  setSearchText, 
  setSearchResults, 
  setIsSearching,
  addToSearchHistory 
} from '../../../../store/slices/searchSlice';
import * as S from '../styles/StudentDashboard.styles';
import SimpleFooter from '../../../../shared/components/layout/SimpleFooter';
import { Calendar, SessionEvent } from '../../../../shared/components/ui/Calendar';
import { 
  UI_TEXT, 
  formatGreeting, 
  SLIDER_CONFIG, 
  SEARCH_CONFIG, 
  DISPLAY_LIMITS 
} from '../../../../constants';
import { parseSafeLocalDate } from '../../../../utils/dateUtils';

export interface ClassDetails {
  classId: number;
  courseId: number;
  name: string;
  desc: string;
  fees: number;
  grade: string;
  tutorId: number;
  tutorName: string;
  fromDate: string | null;
  endDate: string | null;
  subject: string;
  thumbnailUrl?: string;
  rating?: number;
  classNumberText?: string;
  tutorMeta?: string;
  location?: 'online' | 'in person';
  address?: string;
}


const StudentDashboard: React.FC = () => {
  const isAuthenticated = useAuthGuard();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { searchText, searchResults, isSearching: isSearchingRedux } = useSelector((state: RootState) => state.search);
  
  const { data, openMenu, isLoading, error, refetch, categories } = useStudentDashboard();
  const { data: subjects } = useGetSubjectsQuery();
  const [searchCourses, { isLoading: isSearchingAPI }] = useSearchCoursesMutation();
  
  const [currentClassSlide, setCurrentClassSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const CLASSES_PER_SLIDE = 1;
  
  const { 
    data: enrolledClasses, 
    isLoading: isLoadingClasses 
  } = useGetStudentAllClassesQuery(
    { studentId: user?.id || 0 },
    { skip: !user?.id }
  );
  
  const { 
    data: upcomingClasses, 
    isLoading: isLoadingUpcoming 
  } = useGetStudentUpcomingClassesQuery(
    { studentId: user?.id || 0 },
    { skip: !user?.id }
  );
  
  const calendarSessions: SessionEvent[] = enrolledClasses?.map((classItem) => {
    const date = parseSafeLocalDate(classItem.enrolledDate);
    if (!date) return null;
    
    return {
      id: classItem.classId,
      date: date,
      className: classItem.name,
      subject: classItem.subject,
      time: classItem.enrolledSlot || UI_TEXT.status.tbd,
      tutorName: classItem.tutorName,
      classId: classItem.classId,
      courseId: classItem.courseId,
      status: date > new Date() ? UI_TEXT.status.upcoming : UI_TEXT.status.completed,
      type: classItem.location,
      location: classItem.location,
      address: classItem.address
    };
  }).filter(Boolean) || [];

  if (!isAuthenticated) {
    return <CustomLoader visible={true} />;
  }

  const handleClassClick = (classItem: ClassDetails) => {
    navigate(`/class-details/${classItem.classId}`, { 
      state: { classData: classItem } 
    });
  };

  const handleSearchResult = (result: SearchResult) => {
    navigate(`/class-details/${result.classId}`, { 
      state: { classData: result } 
    });
  };

  const handleEnrolledClassClick = (classItem: StudentClass) => {
    navigate(`/class-details/${classItem.classId}`, { 
      state: { 
        classData: {
          classId: classItem.classId,
          courseId: classItem.courseId,
          name: classItem.name,
          subject: classItem.subject,
          tutorName: classItem.tutorName,
          tutorId: classItem.tutorId,
          desc: classItem.desc || '',
          fees: classItem.fees || 0,
          grade: classItem.grade || '',
          fromDate: null,
          endDate: null,
          type: classItem.location,
          location: classItem.location,
          address: classItem.address
        } 
      } 
    });
  };

  const handleQuickSearch = async () => {
    if (!searchText.trim()) return;
    
    dispatch(setIsSearching(true));
    dispatch(addToSearchHistory(searchText.trim()));
    
    try {
      const payload = {
        searchText,
        subject: '',
        minPrice: SEARCH_CONFIG.defaultMinPrice,
        maxPrice: SEARCH_CONFIG.defaultMaxPrice,
        sessionType: SEARCH_CONFIG.defaultSessionType
      };

      const response = await searchCourses(payload).unwrap();
      if (response.data) {
        dispatch(setSearchResults(response.data.arrayObj || []));
      }
    } catch (error) {
      console.error('Search error:', error);
      dispatch(setSearchResults([]));
    } finally {
      dispatch(setIsSearching(false));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuickSearch();
    }
  };

  const handleSessionClick = (session: SessionEvent) => {
    navigate(`/class-details/${session.classId}`, { 
      state: { 
        classData: {
          classId: session.classId,
          courseId: session.courseId,
          name: session.className,
          subject: session.subject,
          tutorName: session.tutorName,
          type: session.location,
          location: session.location,
          address: session.address
        } 
      } 
    });
  };

  const handleSubjectClick = async (subjectName: string) => {
    dispatch(setSearchText(subjectName));
    dispatch(setIsSearching(true));
    dispatch(addToSearchHistory(subjectName));
    
    try {
      const payload = {
        searchText: subjectName,
        subject: '',
        minPrice: SEARCH_CONFIG.defaultMinPrice,
        maxPrice: SEARCH_CONFIG.defaultMaxPrice,
        sessionType: SEARCH_CONFIG.defaultSessionType
      };

      const response = await searchCourses(payload).unwrap();
      if (response.data) {
        dispatch(setSearchResults(response.data.arrayObj || []));
      }
    } catch (error) {
      console.error('Search error:', error);
      dispatch(setSearchResults([]));
    } finally {
      dispatch(setIsSearching(false));
    }
  };

  const handlePointerStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchStart(clientX);
    setTouchEnd(0);
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchEnd(clientX);
  };

  const handlePointerEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > SLIDER_CONFIG.swipeThreshold;
    const isRightSwipe = distance < -SLIDER_CONFIG.swipeThreshold;
    const maxSlides = (data?.length || 0) - 1;

    if (isLeftSwipe && currentClassSlide < maxSlides) {
      setCurrentClassSlide(prev => prev + 1);
    }
    if (isRightSwipe && currentClassSlide > 0) {
      setCurrentClassSlide(prev => prev - 1);
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  if (isLoading && !data?.length) {
    return <CustomLoader visible={isLoading} />;
  }

  if (error && !data?.length) {
    return (
      <>
        <Header onPress={openMenu} />
        <S.DashboardContainer>
          <S.ErrorText>{UI_TEXT.errors.loadClassesFailed}</S.ErrorText>
        </S.DashboardContainer>
      </>
    );
  }

  const totalClasses = data?.length || 0;
  const uniqueSubjects = data ? new Set(data.map(c => c.subject)).size : 0;
  const averagePrice = data?.length ? 
    Math.round(data.reduce((sum, c) => sum + c.fees, 0) / data.length) : 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const todaysClasses = enrolledClasses?.filter((classItem) => {
    const classDate = parseSafeLocalDate(classItem.enrolledDate);
    if (!classDate) return false;
    classDate.setHours(0, 0, 0, 0);
    return classDate.getTime() === today.getTime();
  }) || [];
  
  const sortedTodaysClasses = [...todaysClasses].sort((a, b) => {
    const timeA = a.enrolledSlot || '00:00';
    const timeB = b.enrolledSlot || '00:00';
    return timeA.localeCompare(timeB);
  });

  return (
    <>
      <Header onPress={openMenu} />
      
      <S.DashboardWrapper>
        <S.HeaderBar>
          <S.HeaderGreeting>{formatGreeting('student', user?.firstName)}</S.HeaderGreeting>
        </S.HeaderBar>
        
        <S.ThreeColumnGrid>
          <S.DashboardColumn $expanded>
            <S.ColumnHeader>
              <S.ColumnIcon>{UI_TEXT.icons.chart}</S.ColumnIcon>
              <S.ColumnTitle>{UI_TEXT.columnTitles.learningOverview}</S.ColumnTitle>
            </S.ColumnHeader>
            
            <S.ColumnContent>
              <S.QuickStats>
                <S.MiniStatCard $color="#ff6b6b">
                  <S.MiniStatValue>{totalClasses}</S.MiniStatValue>
                  <S.MiniStatLabel>{UI_TEXT.statLabels.availableClasses}</S.MiniStatLabel>
                </S.MiniStatCard>
                <S.MiniStatCard $color="#4ecdc4">
                  <S.MiniStatValue>{uniqueSubjects}</S.MiniStatValue>
                  <S.MiniStatLabel>{UI_TEXT.statLabels.subjects}</S.MiniStatLabel>
                </S.MiniStatCard>
              </S.QuickStats>
              
              <S.TodayScheduleSection>
                <S.TodayScheduleHeader>
                  <S.TodayScheduleTitle>
                    <S.ScheduleIcon>📅</S.ScheduleIcon>
                    Today's Schedule
                  </S.TodayScheduleTitle>
                  <S.TodayDate>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</S.TodayDate>
                </S.TodayScheduleHeader>
                
                {isLoadingClasses ? (
                  <S.LoadingBlock>
                    <CustomLoader visible={true} />
                  </S.LoadingBlock>
                ) : sortedTodaysClasses.length > 0 ? (
                  <S.TodayScheduleList>
                    {sortedTodaysClasses.map((classItem) => (
                      <S.TodayScheduleItem key={classItem.classId} onClick={() => handleEnrolledClassClick(classItem)}>
                        <S.ScheduleTime>{classItem.enrolledSlot || 'TBD'}</S.ScheduleTime>
                        <S.ScheduleDetails>
                          <S.ScheduleClassName>{classItem.name}</S.ScheduleClassName>
                          <S.ScheduleSubject>
                            {classItem.subject} • {classItem.tutorName}
                            {classItem.location && ` • ${classItem.location === 'online' ? '🌐' : '📍'}`}
                          </S.ScheduleSubject>
                        </S.ScheduleDetails>
                        <S.ScheduleArrow>➤</S.ScheduleArrow>
                      </S.TodayScheduleItem>
                    ))}
                  </S.TodayScheduleList>
                ) : (
                  <S.TodayScheduleEmpty>
                    <S.EmptyScheduleIcon>🗓️</S.EmptyScheduleIcon>
                    <S.EmptyScheduleText>No classes scheduled for today</S.EmptyScheduleText>
                  </S.TodayScheduleEmpty>
                )}
              </S.TodayScheduleSection>
              
              <S.SectionDivider />
              <S.UpcomingClassesSection>
                <S.SectionHeader>
                  <S.SectionTitle>
                    <S.ScheduleIcon>📚</S.ScheduleIcon>
                    Upcoming Classes
                  </S.SectionTitle>
    
                </S.SectionHeader>
                
                {isLoadingUpcoming ? (
                  <S.LoadingBlock>
                    <CustomLoader visible={true} />
                  </S.LoadingBlock>
                ) : upcomingClasses && upcomingClasses.length > 0 ? (
                  <S.UpcomingClassesList>
                    {upcomingClasses.slice(0, 5).map((classItem) => {
                      const classDate = parseSafeLocalDate(classItem.enrolledDate);
                      if (!classDate) return null;
                      const dayName = classDate.toLocaleDateString('en-US', { weekday: 'short' });
                      const monthDay = classDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      
                      return (
                        <S.UpcomingClassItem key={classItem.classId} >{/**onClick={() => handleEnrolledClassClick(classItem)} */}
                          <S.DateBadge>
                            <S.DateDay>{dayName}</S.DateDay>
                            <S.DateMonthDay>{monthDay}</S.DateMonthDay>
                          </S.DateBadge>
                          <S.UpcomingClassDetails>
                            <S.UpcomingClassName>{classItem.name}</S.UpcomingClassName>
                            <S.UpcomingClassMeta>
                              {classItem.enrolledSlot || 'Time TBD'} • {classItem.subject} • {classItem.tutorName}
                              {classItem.location && ` • ${classItem.location === 'online' ? '🌐' : '📍'}`}
                            </S.UpcomingClassMeta>
                          </S.UpcomingClassDetails>
                          {/**<S.ScheduleArrow className="schedule-arrow">➤</S.ScheduleArrow> */}
                        </S.UpcomingClassItem>
                      );
                    })}
                  </S.UpcomingClassesList>
                ) : (
                  <S.UpcomingClassesEmpty>
                    <S.EmptyScheduleIcon>📅</S.EmptyScheduleIcon>
                    <S.EmptyScheduleText>No upcoming classes</S.EmptyScheduleText>
                  </S.UpcomingClassesEmpty>
                )}
              </S.UpcomingClassesSection>
            </S.ColumnContent>
          </S.DashboardColumn>

          <S.DashboardColumn>
            <S.ColumnHeader>
              <S.ColumnIcon>{UI_TEXT.icons.search}</S.ColumnIcon>
              <S.ColumnTitle>{UI_TEXT.columnTitles.quickSearch}</S.ColumnTitle>
            </S.ColumnHeader>
            
            <S.ColumnContent>
              <S.SearchSection>
                <S.SearchInputWrapper>
                  <S.SearchInput
                    type="text"
                    placeholder={UI_TEXT.placeholders.search}
                    value={searchText}
                    onChange={(e) => dispatch(setSearchText(e.target.value))}
                    onKeyPress={handleKeyPress}
                  />
                  <S.SearchIconButton onClick={handleQuickSearch} disabled={isSearchingRedux || isSearchingAPI}>
                    {isSearchingRedux || isSearchingAPI ? '⏳' : UI_TEXT.icons.search}
                  </S.SearchIconButton>
                </S.SearchInputWrapper>

                {searchResults.length > 0 && (
                  <S.SearchResultsSection>
                    <S.SectionSubtitle>{UI_TEXT.sectionTitles.searchResults} ({searchResults.length})</S.SectionSubtitle>
                    <S.SearchResultsList>
                      {searchResults.slice(0, DISPLAY_LIMITS.searchResultsPreview).map((result) => (
                        <S.SearchResultItem 
                          key={`${result.classId}-${result.courseId}`}
                          onClick={() => handleSearchResult(result)}
                        >
                          <S.ResultInfo>
                            <S.ResultTitle>{result.name}</S.ResultTitle>
                            <S.ResultMeta>
                              by {result.tutorName} • {result.subject}
                              {result.location && ` • ${result.location === 'online' ? '🌐 Online' : '📍 In-Person'}`}
                            </S.ResultMeta>
                          </S.ResultInfo>
                          <S.ResultPrice>${result.fees}</S.ResultPrice>
                        </S.SearchResultItem>
                      ))}
                    </S.SearchResultsList>
                    {searchResults.length > DISPLAY_LIMITS.searchResultsPreview && (
                      <S.ViewAllLink onClick={() => navigate('/search', { state: { from: 'student-dashboard' } })}>
                        {UI_TEXT.search.viewAllResults}
                        <S.ArrowIcon>{UI_TEXT.icons.arrow}</S.ArrowIcon>
                      </S.ViewAllLink>
                    )}
                  </S.SearchResultsSection>
                )}

                <S.AdvancedSearchLink onClick={() => {
                  console.log('Navigating to search from student dashboard');
                  navigate('/search', { state: { from: 'student-dashboard' } });
                }}>
                  <S.AdvancedSearchIcon>{UI_TEXT.search.advancedSearchIcon}</S.AdvancedSearchIcon>
                  {UI_TEXT.buttons.advancedSearch}
                </S.AdvancedSearchLink>
              </S.SearchSection>
            </S.ColumnContent>
          </S.DashboardColumn>

          <S.DashboardColumn>
            <S.ColumnHeader>
              <S.ColumnIcon>{UI_TEXT.icons.calendar}</S.ColumnIcon>
              <S.ColumnTitle>{UI_TEXT.columnTitles.myClasses}</S.ColumnTitle>
            </S.ColumnHeader>
            
            <S.ColumnContent>
              {isLoadingClasses ? (
                <S.LoadingBlock>
                  <CustomLoader visible={true} />
                </S.LoadingBlock>
              ) : (
                <Calendar
                  sessions={calendarSessions}
                  onSessionClick={handleSessionClick}
                  userType="student"
                />
              )}
            </S.ColumnContent>
          </S.DashboardColumn>
          {/* Available Classes Column */}
          <S.DashboardColumn>
            <S.ColumnHeader>
              <S.ColumnIcon>📚</S.ColumnIcon>
              <S.ColumnTitle>Available Classes</S.ColumnTitle>
            </S.ColumnHeader>
            
            <S.ColumnContent>
              {data && data.length > 0 ? (
                <>
                  <S.ClassesGrid>
                    {data.slice(0, 3).map((classItem: ClassDetails) => (
                      <S.ClassGridItem key={classItem.classId}>
                        <ClassItem item={classItem} onClassClick={handleClassClick} />
                      </S.ClassGridItem>
                    ))}
                  </S.ClassesGrid>
                  {data.length > 3 && (
                    <S.ViewAllLink onClick={() => navigate('/all-classes')} style={{ marginTop: 'auto', justifyContent: 'center' }}>
                      View All {data.length} Classes
                      <S.ArrowIcon>{UI_TEXT.icons.arrow}</S.ArrowIcon>
                    </S.ViewAllLink>
                  )}
                </>
              ) : (
                !isLoading && (
                  <S.EmptyBlockState>
                    <S.EmptyIcon>{UI_TEXT.emptyStates.noClasses.icon}</S.EmptyIcon>
                    <S.EmptyText>{UI_TEXT.emptyStates.noClasses.title}</S.EmptyText>
                    <S.EmptySubText>{UI_TEXT.emptyStates.noClasses.subtitle}</S.EmptySubText>
                  </S.EmptyBlockState>
                )
              )}
            </S.ColumnContent>
          </S.DashboardColumn>
        </S.ThreeColumnGrid>
      </S.DashboardWrapper>
      
      <SimpleFooter />
    </>
  );
};

export default StudentDashboard;